import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Download, Search } from 'lucide-react';

import { useActiveOrders, usePharmacyEvents } from '@/lib/api/adminHooks';
import { downloadCsv } from '@/lib/csv';
import type {
  ClinicalOrderSummary,
  DispenseEvent,
  StageBucket,
  SubmissionEvent,
} from '@/types/admin';

/**
 * Per-table detail view for clinical-ops. Each summary table on
 * /admin/clinical-ops links here with a `view` URL param picking the data
 * source. Filters + CSV export live here so the summary screen stays a
 * one-screen at-a-glance and the detail pages can hold the heavier UI.
 */
export function ClinicalOpsDetailScreen() {
  const { view } = useParams<{ view: string }>();
  switch (view) {
    case 'awaiting-script':
      return (
        <ActiveOrdersDetail
          bucketKey="awaiting_script"
          title="Awaiting script"
          description="Orders sitting in the provider review queue past the 48-hour threshold."
          showHours
        />
      );
    case 'awaiting-shipment':
      return (
        <ActiveOrdersDetail
          bucketKey="awaiting_shipment"
          title="Awaiting shipment"
          description="Scripts written; orders queued at the pharmacy past the 24-hour threshold."
          showHours
        />
      );
    case 'failed-renewals':
      return (
        <ActiveOrdersDetail
          bucketKey="failed_renewals"
          title="Failed renewals"
          description="Subscription cycle orders whose latest charge attempt failed."
          showHours={false}
        />
      );
    case 'dispenses':
      return (
        <PharmacyEventsDetail
          eventType="dispenses"
          title="Units dispensed"
          description="Per line-item dispense events (status → shipped). Filter by SKU, pharmacy, customer, or date range; export the filtered set."
        />
      );
    case 'completions':
      return (
        <PharmacyEventsDetail
          eventType="completions"
          title="Units completed"
          description="Per line-item completion events (status → completed). Filter and export the filtered set."
        />
      );
    case 'submissions':
      return (
        <PharmacySubmissionsDetail
          title="Orders submitted"
          description="Every order × pharmacy assignment, keyed off order creation time. Filter by pharmacy, customer, source, or date range."
        />
      );
    default:
      return <Navigate to="/admin/clinical-ops" replace />;
  }
}

// ----- Active orders detail -----

type ActiveBucketKey =
  | 'awaiting_script'
  | 'awaiting_shipment'
  | 'failed_renewals';

function ActiveOrdersDetail({
  bucketKey,
  title,
  description,
  showHours,
}: {
  bucketKey: ActiveBucketKey;
  title: string;
  description: string;
  showHours: boolean;
}) {
  const { data, isLoading, isError } = useActiveOrders();
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');

  const bucket: StageBucket | undefined = data?.[bucketKey];

  const sources = useMemo(() => {
    const seen = new Set<string>();
    for (const o of bucket?.orders ?? []) {
      if (o.source) seen.add(o.source);
    }
    return [...seen].sort();
  }, [bucket]);

  const filtered = useMemo(() => {
    if (!bucket) return [];
    const s = search.trim().toLowerCase();
    const p = productFilter.trim().toLowerCase();
    const after = createdAfter ? new Date(`${createdAfter}T00:00:00`).getTime() : null;
    const before = createdBefore
      ? new Date(`${createdBefore}T23:59:59.999`).getTime()
      : null;
    return bucket.orders.filter((o) => {
      if (s) {
        const hay = `${o.customer_email ?? ''} ${o.customer_cp_id ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (p) {
        if (!(o.product_label ?? '').toLowerCase().includes(p)) return false;
      }
      if (sourceFilter && o.source !== sourceFilter) return false;
      if (after != null) {
        const t = o.created_at ? new Date(o.created_at).getTime() : null;
        if (t == null || t < after) return false;
      }
      if (before != null) {
        const t = o.created_at ? new Date(o.created_at).getTime() : null;
        if (t == null || t > before) return false;
      }
      return true;
    });
  }, [bucket, search, productFilter, sourceFilter, createdAfter, createdBefore]);

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const cols = [
      { key: 'customer_email', label: 'Customer email' },
      { key: 'customer_cp_id', label: 'Customer CP ID' },
      { key: 'product_label', label: 'Product' },
      { key: 'source', label: 'Source' },
      ...(showHours
        ? [{ key: 'hours_in_stage', label: 'Hours in stage' }]
        : []),
      { key: 'status', label: 'Status' },
      { key: 'payment_status', label: 'Payment status' },
      { key: 'amount_cents', label: 'Amount (cents)' },
      { key: 'created_at', label: 'Created at' },
      { key: 'cp_id', label: 'Order CP ID' },
    ];
    downloadCsv(
      `${bucketKey}-${stamp}.csv`,
      cols as { key: keyof ClinicalOrderSummary & string; label: string }[],
      filtered
    );
  };

  return (
    <DetailShell
      title={title}
      description={description}
      crumb="Active orders"
      crumbHref="/admin/clinical-ops"
      isLoading={isLoading && !data}
      isError={isError}
      total={filtered.length}
      onExport={handleExport}
      filters={
        <FilterBar>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search customer email or ID"
          />
          <TextInput
            label="Product"
            value={productFilter}
            onChange={setProductFilter}
            placeholder="contains…"
          />
          {sources.length > 1 && (
            <SelectInput
              label="Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              options={[
                { label: 'All', value: '' },
                ...sources.map((s) => ({ label: s, value: s })),
              ]}
            />
          )}
          <DateInput
            label="Created after"
            value={createdAfter}
            onChange={setCreatedAfter}
          />
          <DateInput
            label="Before"
            value={createdBefore}
            onChange={setCreatedBefore}
          />
          {(search ||
            productFilter ||
            sourceFilter ||
            createdAfter ||
            createdBefore) && (
            <ClearFilters
              onClick={() => {
                setSearch('');
                setProductFilter('');
                setSourceFilter('');
                setCreatedAfter('');
                setCreatedBefore('');
              }}
            />
          )}
        </FilterBar>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <table className="w-full type-label-sm">
          <thead className="bg-sunken">
            <tr className="text-left text-ink-muted">
              <Th>Customer</Th>
              <Th>Product</Th>
              <Th>Source</Th>
              {showHours && <Th align="right">Hours in stage</Th>}
              <Th>Status</Th>
              <Th>Payment</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr
                key={o.cp_id}
                className="border-t border-line-subtle hover:bg-sunken"
              >
                <Td>
                  {o.customer_cp_id ? (
                    <Link
                      to={`/admin/customers/${o.customer_cp_id}`}
                      className="underline decoration-line underline-offset-4 hover:decoration-ink"
                    >
                      {o.customer_email || o.customer_cp_id}
                    </Link>
                  ) : (
                    o.customer_email || <span className="text-ink-muted">—</span>
                  )}
                </Td>
                <Td>{o.product_label || '—'}</Td>
                <Td>
                  <span className="text-ink-muted">{o.source ?? '—'}</span>
                </Td>
                {showHours && (
                  <Td align="right">
                    {o.hours_in_stage != null
                      ? `${o.hours_in_stage.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}h`
                      : '—'}
                  </Td>
                )}
                <Td>{o.status ?? '—'}</Td>
                <Td>{o.payment_status ?? '—'}</Td>
                <Td>
                  {o.created_at
                    ? format(parseISO(o.created_at), 'MMM d, yyyy')
                    : '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DetailShell>
  );
}

// ----- Pharmacy events detail (dispenses + completions) -----

function PharmacyEventsDetail({
  eventType,
  title,
  description,
}: {
  eventType: 'dispenses' | 'completions';
  title: string;
  description: string;
}) {
  const { data, isLoading, isError } = usePharmacyEvents();
  const events: DispenseEvent[] =
    eventType === 'dispenses'
      ? data?.dispense_events ?? []
      : data?.completion_events ?? [];
  const tsField: 'shipped_at' | 'completed_at' =
    eventType === 'dispenses' ? 'shipped_at' : 'completed_at';
  const tsLabel = eventType === 'dispenses' ? 'Shipped' : 'Completed';

  const [search, setSearch] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  const [pharmacyFilter, setPharmacyFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [tsAfter, setTsAfter] = useState('');
  const [tsBefore, setTsBefore] = useState('');

  const pharmacies = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) s.add(e.pharmacy);
    return [...s].sort();
  }, [events]);

  const sources = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) {
      if (e.source) s.add(e.source);
    }
    return [...s].sort();
  }, [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sk = skuFilter.trim().toLowerCase();
    const after = tsAfter ? new Date(`${tsAfter}T00:00:00`).getTime() : null;
    const before = tsBefore
      ? new Date(`${tsBefore}T23:59:59.999`).getTime()
      : null;
    return events.filter((e) => {
      if (q) {
        const hay = `${e.customer_email ?? ''} ${e.customer_cp_id ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (sk) {
        if (!(e.sku ?? '').toLowerCase().includes(sk)) return false;
      }
      if (pharmacyFilter && e.pharmacy !== pharmacyFilter) return false;
      if (sourceFilter && e.source !== sourceFilter) return false;
      const tsRaw = e[tsField];
      if (after != null) {
        const t = tsRaw ? new Date(tsRaw).getTime() : null;
        if (t == null || t < after) return false;
      }
      if (before != null) {
        const t = tsRaw ? new Date(tsRaw).getTime() : null;
        if (t == null || t > before) return false;
      }
      return true;
    });
  }, [events, search, skuFilter, pharmacyFilter, sourceFilter, tsAfter, tsBefore, tsField]);

  const totalUnits = filtered.reduce((acc, r) => acc + (r.quantity || 0), 0);

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const cols = [
      { key: 'customer_email', label: 'Customer email' },
      { key: 'customer_cp_id', label: 'Customer CP ID' },
      { key: 'sku', label: 'SKU' },
      { key: 'pharmacy', label: 'Pharmacy' },
      { key: 'quantity', label: 'Quantity' },
      { key: tsField, label: tsLabel },
      { key: 'order_created_at', label: 'Order created' },
      { key: 'source', label: 'Source' },
      { key: 'order_status', label: 'Order status' },
      { key: 'order_cp_id', label: 'Order CP ID' },
    ];
    downloadCsv<DispenseEvent>(
      `${eventType}-${stamp}.csv`,
      cols as { key: keyof DispenseEvent & string; label: string }[],
      filtered
    );
  };

  return (
    <DetailShell
      title={title}
      description={description}
      crumb="Pharmacy"
      crumbHref="/admin/clinical-ops"
      isLoading={isLoading && !data}
      isError={isError}
      total={filtered.length}
      extraSummary={`${totalUnits.toLocaleString()} units`}
      onExport={handleExport}
      filters={
        <FilterBar>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search customer email or ID"
          />
          <TextInput
            label="SKU"
            value={skuFilter}
            onChange={setSkuFilter}
            placeholder="contains…"
          />
          {pharmacies.length > 1 && (
            <SelectInput
              label="Pharmacy"
              value={pharmacyFilter}
              onChange={setPharmacyFilter}
              options={[
                { label: 'All', value: '' },
                ...pharmacies.map((p) => ({ label: p, value: p })),
              ]}
            />
          )}
          {sources.length > 1 && (
            <SelectInput
              label="Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              options={[
                { label: 'All', value: '' },
                ...sources.map((s) => ({ label: s, value: s })),
              ]}
            />
          )}
          <DateInput
            label={`${tsLabel} after`}
            value={tsAfter}
            onChange={setTsAfter}
          />
          <DateInput label="Before" value={tsBefore} onChange={setTsBefore} />
          {(search ||
            skuFilter ||
            pharmacyFilter ||
            sourceFilter ||
            tsAfter ||
            tsBefore) && (
            <ClearFilters
              onClick={() => {
                setSearch('');
                setSkuFilter('');
                setPharmacyFilter('');
                setSourceFilter('');
                setTsAfter('');
                setTsBefore('');
              }}
            />
          )}
        </FilterBar>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <table className="w-full type-label-sm">
          <thead className="bg-sunken">
            <tr className="text-left text-ink-muted">
              <Th>Customer</Th>
              <Th>SKU</Th>
              <Th>Pharmacy</Th>
              <Th align="right">Qty</Th>
              <Th>{tsLabel}</Th>
              <Th>Order created</Th>
              <Th>Source</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr
                key={`${e.order_cp_id}-${i}`}
                className="border-t border-line-subtle hover:bg-sunken"
              >
                <Td>
                  {e.customer_cp_id ? (
                    <Link
                      to={`/admin/customers/${e.customer_cp_id}`}
                      className="underline decoration-line underline-offset-4 hover:decoration-ink"
                    >
                      {e.customer_email || e.customer_cp_id}
                    </Link>
                  ) : (
                    e.customer_email || <span className="text-ink-muted">—</span>
                  )}
                </Td>
                <Td>{e.sku || '—'}</Td>
                <Td>{e.pharmacy}</Td>
                <Td align="right">{(e.quantity || 0).toLocaleString()}</Td>
                <Td>
                  {e[tsField]
                    ? format(parseISO(e[tsField] as string), 'MMM d, yyyy')
                    : '—'}
                </Td>
                <Td>
                  {e.order_created_at
                    ? format(parseISO(e.order_created_at), 'MMM d, yyyy')
                    : '—'}
                </Td>
                <Td>
                  <span className="text-ink-muted">{e.source ?? '—'}</span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DetailShell>
  );
}

// ----- Pharmacy submissions detail -----

function PharmacySubmissionsDetail({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const { data, isLoading, isError } = usePharmacyEvents();
  const events: SubmissionEvent[] = data?.submission_events ?? [];

  const [search, setSearch] = useState('');
  const [pharmacyFilter, setPharmacyFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');

  const pharmacies = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) s.add(e.pharmacy);
    return [...s].sort();
  }, [events]);
  const sources = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) {
      if (e.source) s.add(e.source);
    }
    return [...s].sort();
  }, [events]);
  const statuses = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) {
      if (e.order_status) s.add(e.order_status);
    }
    return [...s].sort();
  }, [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const after = createdAfter
      ? new Date(`${createdAfter}T00:00:00`).getTime()
      : null;
    const before = createdBefore
      ? new Date(`${createdBefore}T23:59:59.999`).getTime()
      : null;
    return events.filter((e) => {
      if (q) {
        const hay = `${e.customer_email ?? ''} ${e.customer_cp_id ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (pharmacyFilter && e.pharmacy !== pharmacyFilter) return false;
      if (sourceFilter && e.source !== sourceFilter) return false;
      if (statusFilter && e.order_status !== statusFilter) return false;
      if (after != null) {
        const t = e.order_created_at
          ? new Date(e.order_created_at).getTime()
          : null;
        if (t == null || t < after) return false;
      }
      if (before != null) {
        const t = e.order_created_at
          ? new Date(e.order_created_at).getTime()
          : null;
        if (t == null || t > before) return false;
      }
      return true;
    });
  }, [
    events,
    search,
    pharmacyFilter,
    sourceFilter,
    statusFilter,
    createdAfter,
    createdBefore,
  ]);

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const cols = [
      { key: 'customer_email', label: 'Customer email' },
      { key: 'customer_cp_id', label: 'Customer CP ID' },
      { key: 'pharmacy', label: 'Pharmacy' },
      { key: 'order_created_at', label: 'Order created' },
      { key: 'source', label: 'Source' },
      { key: 'order_status', label: 'Order status' },
      { key: 'order_cp_id', label: 'Order CP ID' },
    ];
    downloadCsv<SubmissionEvent>(
      `submissions-${stamp}.csv`,
      cols as { key: keyof SubmissionEvent & string; label: string }[],
      filtered
    );
  };

  return (
    <DetailShell
      title={title}
      description={description}
      crumb="Pharmacy"
      crumbHref="/admin/clinical-ops"
      isLoading={isLoading && !data}
      isError={isError}
      total={filtered.length}
      onExport={handleExport}
      filters={
        <FilterBar>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search customer email or ID"
          />
          {pharmacies.length > 1 && (
            <SelectInput
              label="Pharmacy"
              value={pharmacyFilter}
              onChange={setPharmacyFilter}
              options={[
                { label: 'All', value: '' },
                ...pharmacies.map((p) => ({ label: p, value: p })),
              ]}
            />
          )}
          {sources.length > 1 && (
            <SelectInput
              label="Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              options={[
                { label: 'All', value: '' },
                ...sources.map((s) => ({ label: s, value: s })),
              ]}
            />
          )}
          {statuses.length > 1 && (
            <SelectInput
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: 'All', value: '' },
                ...statuses.map((s) => ({ label: s, value: s })),
              ]}
            />
          )}
          <DateInput
            label="Created after"
            value={createdAfter}
            onChange={setCreatedAfter}
          />
          <DateInput
            label="Before"
            value={createdBefore}
            onChange={setCreatedBefore}
          />
          {(search ||
            pharmacyFilter ||
            sourceFilter ||
            statusFilter ||
            createdAfter ||
            createdBefore) && (
            <ClearFilters
              onClick={() => {
                setSearch('');
                setPharmacyFilter('');
                setSourceFilter('');
                setStatusFilter('');
                setCreatedAfter('');
                setCreatedBefore('');
              }}
            />
          )}
        </FilterBar>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <table className="w-full type-label-sm">
          <thead className="bg-sunken">
            <tr className="text-left text-ink-muted">
              <Th>Customer</Th>
              <Th>Pharmacy</Th>
              <Th>Order created</Th>
              <Th>Source</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr
                key={`${e.order_cp_id}-${i}`}
                className="border-t border-line-subtle hover:bg-sunken"
              >
                <Td>
                  {e.customer_cp_id ? (
                    <Link
                      to={`/admin/customers/${e.customer_cp_id}`}
                      className="underline decoration-line underline-offset-4 hover:decoration-ink"
                    >
                      {e.customer_email || e.customer_cp_id}
                    </Link>
                  ) : (
                    e.customer_email || <span className="text-ink-muted">—</span>
                  )}
                </Td>
                <Td>{e.pharmacy}</Td>
                <Td>
                  {e.order_created_at
                    ? format(parseISO(e.order_created_at), 'MMM d, yyyy')
                    : '—'}
                </Td>
                <Td>
                  <span className="text-ink-muted">{e.source ?? '—'}</span>
                </Td>
                <Td>{e.order_status ?? '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DetailShell>
  );
}

// ----- Shared shell + filter primitives -----

function DetailShell({
  title,
  description,
  crumb,
  crumbHref,
  isLoading,
  isError,
  total,
  extraSummary,
  filters,
  onExport,
  children,
}: {
  title: string;
  description: string;
  crumb: string;
  crumbHref: string;
  isLoading: boolean;
  isError: boolean;
  total: number;
  extraSummary?: string;
  filters: React.ReactNode;
  onExport: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <Link
          to={crumbHref}
          className="type-label-sm text-ink-muted hover:text-ink underline decoration-line underline-offset-4 self-start"
        >
          ← {crumb}
        </Link>
        <h1 className="type-heading-xl">{title}</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">{description}</p>
      </div>

      <div className="p-12 border border-line rounded-lg flex flex-col gap-12">
        <div className="flex items-center gap-12">
          <span className="type-label-sm text-ink-muted">
            {total.toLocaleString()} results{extraSummary ? ` · ${extraSummary}` : ''}
          </span>
          <button
            type="button"
            onClick={onExport}
            disabled={isLoading || isError || total === 0}
            className="ml-auto inline-flex items-center gap-6 px-12 py-6 rounded border border-line hover:bg-raised disabled:opacity-50 type-label-sm"
          >
            <Download size={14} strokeWidth={1.5} />
            Export CSV
          </button>
        </div>
        {filters}
      </div>

      {isError && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md">
          Failed to load data.
        </div>
      )}
      {isLoading ? (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading…
        </div>
      ) : (
        <div className="border border-line rounded-lg bg-raised overflow-hidden">
          <div className="overflow-x-auto">{children}</div>
        </div>
      )}
    </div>
  );
}

function FilterBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-12 type-label-sm">
      {children}
    </div>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="flex items-center gap-6 bg-raised border border-line rounded px-8 py-4">
      <Search size={14} strokeWidth={1.5} className="text-ink-muted" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent outline-none w-[240px]"
      />
    </label>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-6">
      <span className="text-ink-muted">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-raised border border-line rounded px-8 py-4 w-[180px]"
      />
    </label>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <label className="flex items-center gap-6">
      <span className="text-ink-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-raised border border-line rounded px-8 py-4"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-6">
      <span className="text-ink-muted">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-raised border border-line rounded px-8 py-4"
      />
    </label>
  );
}

function ClearFilters({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-10 py-4 text-ink-muted hover:text-ink ml-auto"
    >
      Clear filters
    </button>
  );
}

function EmptyState() {
  return (
    <div className="p-16 text-center text-ink-muted type-label-sm">
      No matching records.
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  return (
    <td
      className={`px-12 py-8 ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
      }`}
    >
      {children}
    </td>
  );
}
